import { useEffect } from 'react';
import {
  PermissionGroupsParams,
  PermissionGroupsResponse,
} from 'src/apis/authorization-service/types';
import { AuthorizationServiceRequest } from 'src/apis/authorization-service/use-authorization-service';
import { Successful, useAxios } from 'src/hooks/use-axios';
import { Forbidden } from 'src/utils/errors';
import urlTemplate from 'url-template';

type HookProps = {
  authorizationService?: AuthorizationServiceRequest;
  params?: PermissionGroupsParams;
};

type ExecuteProps = {
  authorizationService: Successful<AuthorizationServiceRequest>;
  params?: PermissionGroupsParams;
};

export function usePermissionGroups({
  authorizationService,
  params,
}: HookProps) {
  const request = useAxios(
    (axios, signal, { authorizationService, params }: ExecuteProps) => {
      if (!authorizationService.response.data._links.permissionGroups)
        throw new Forbidden();

      const url = urlTemplate
        .parse(authorizationService.response.data._links.permissionGroups.href)
        .expand({});

      return axios.request<PermissionGroupsResponse>({
        signal,
        url,
        params,
      });
    },
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    if (!authorizationService?.response) return;
    request.execute({ authorizationService, params });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorizationService?.response, params]);

  if (!authorizationService) return;

  return request;
}

export type PermissionGroupsRequest = ReturnType<typeof usePermissionGroups>;
